import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Settings, FileText, Shield, DollarSign } from 'lucide-react';
import { Card, Badge, Button, Modal } from '../../components/ui';
import { api } from '../../lib/api';

interface AssetTypeConfig {
  id: string;
  category: string;
  name: string;
  description: string;
  requiredDocuments: string[];
  custodyRules: Record<string, any>;
  complianceRules: Record<string, any>;
  defaultYieldConfig: Record<string, any>;
  isActive: boolean;
  createdAt: string;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundos',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios'
};

export function AssetTypesManagement() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<AssetTypeConfig | null>(null);
  const [formData, setFormData] = useState({
    category: 'STABLECOIN',
    name: '',
    description: '',
    requiredDocuments: [] as string[],
    isActive: true
  });

  const { data: assetTypes, isLoading } = useQuery({
    queryKey: ['asset-types'],
    queryFn: async () => {
      const res = await api.get<AssetTypeConfig[]>('/tokenization/asset-types');
      return res.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post('/tokenization/asset-types', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-types'] });
      setShowModal(false);
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: typeof formData }) => 
      api.put(`/tokenization/asset-types/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset-types'] });
      setShowModal(false);
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/tokenization/asset-types/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['asset-types'] })
  });

  const resetForm = () => {
    setFormData({
      category: 'STABLECOIN',
      name: '',
      description: '',
      requiredDocuments: [],
      isActive: true
    });
    setEditingType(null);
  };

  const handleEdit = (type: AssetTypeConfig) => {
    setEditingType(type);
    setFormData({
      category: type.category,
      name: type.name,
      description: type.description,
      requiredDocuments: type.requiredDocuments,
      isActive: type.isActive
    });
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingType) {
      updateMutation.mutate({ id: editingType.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const documentOptions = [
    'CONTRATO_SOCIAL', 'CNPJ', 'PROCURACAO', 'LAUDO_AVALIACAO',
    'CERTIDAO_MATRICULA', 'ESCRITURA', 'BALANCO_PATRIMONIAL',
    'DRE', 'REGULAMENTO_FUNDO', 'PROSPECTO', 'NOTA_FISCAL',
    'CONTRATO_CESSAO', 'SENTENCA_JUDICIAL', 'CERTIDAO_PRECATORIO'
  ];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-48 bg-surface-800 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Tipos de Ativos</h1>
          <p className="text-surface-400">Configure os tipos de ativos tokenizáveis da plataforma</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Tipo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {assetTypes?.map((type) => (
          <Card key={type.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <Badge variant={type.isActive ? 'success' : 'default'}>
                  {categoryLabels[type.category]}
                </Badge>
                <h3 className="font-semibold text-surface-100 mt-2">{type.name}</h3>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(type)}
                  className="p-2 hover:bg-surface-700 rounded-lg transition-colors"
                >
                  <Edit2 className="w-4 h-4 text-surface-400" />
                </button>
                <button
                  onClick={() => deleteMutation.mutate(type.id)}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            </div>

            <p className="text-sm text-surface-400 mb-4 line-clamp-2">{type.description}</p>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-surface-400">
                <FileText className="w-4 h-4" />
                <span>{type.requiredDocuments.length} documentos obrigatórios</span>
              </div>
              <div className="flex items-center gap-2 text-surface-400">
                <Shield className="w-4 h-4" />
                <span>Regras de compliance configuradas</span>
              </div>
              <div className="flex items-center gap-2 text-surface-400">
                <DollarSign className="w-4 h-4" />
                <span>Yield configurável</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Modal de Criação/Edição */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingType ? 'Editar Tipo de Ativo' : 'Novo Tipo de Ativo'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-surface-400 mb-1">Categoria</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
            >
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Nome</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
              placeholder="Ex: Token de Recebíveis Comerciais"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-1">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
              rows={3}
              placeholder="Descreva o tipo de ativo..."
            />
          </div>

          <div>
            <label className="block text-sm text-surface-400 mb-2">Documentos Obrigatórios</label>
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {documentOptions.map((doc) => (
                <label key={doc} className="flex items-center gap-2 text-sm text-surface-300">
                  <input
                    type="checkbox"
                    checked={formData.requiredDocuments.includes(doc)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, requiredDocuments: [...formData.requiredDocuments, doc] });
                      } else {
                        setFormData({ ...formData, requiredDocuments: formData.requiredDocuments.filter(d => d !== doc) });
                      }
                    }}
                    className="rounded border-surface-600"
                  />
                  {doc.replace(/_/g, ' ')}
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded border-surface-600"
            />
            <label htmlFor="isActive" className="text-sm text-surface-300">Ativo</label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {editingType ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
